package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

import com.vidaensupalabra.vsp.datos.MultimediaItem
import com.vidaensupalabra.vsp.datos.MultimediaRepository
import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.White

@Composable
fun MultimediaScreen(onBack: () -> Unit) {
    val multimediaItems by MultimediaRepository.multimediaFlow.collectAsState()

    LaunchedEffect(Unit) {
        MultimediaRepository.loadDataIfNeeded()
    }

    // Memoización para evitar recalcular filtros en recomposiciones
    val imageItems = remember(multimediaItems) { multimediaItems.filter { it.mimeType.startsWith("image/") } }
    val videoItems = remember(multimediaItems) { multimediaItems.filter { it.mimeType.startsWith("video/") } }

    var selectedTabIndex by remember { mutableStateOf(0) }
    val currentList = if (selectedTabIndex == 0) imageItems else videoItems

    val groupedByMonth = remember(currentList) { currentList.groupBy { it.date?.take(7) } }
    var selectedMonth by remember { mutableStateOf<String?>(null) }

    // Contenedor principal de la pantalla
    Div(attrs = {
        style {
            width(100.percent)
            minHeight(100.vh)
            backgroundColor(VspBase)
            fontFamily("sans-serif")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        MultimediaTopAppBar(
            title = if (selectedMonth == null) "Multimedia" else "Mes: $selectedMonth",
            onBack = {
                if (selectedMonth != null) {
                    selectedMonth = null
                } else {
                    onBack()
                }
            }
        )

        Div(attrs = {
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                padding(16.px)
                boxSizing("border-box")
            }
        }) {
            MultimediaTabs(
                selectedTabIndex = selectedTabIndex,
                onTabSelected = { newIndex ->
                    selectedTabIndex = newIndex
                    selectedMonth = null
                }
            )
            
            if (selectedMonth == null) {
                MonthFoldersScreen(
                    groupedByMonth = groupedByMonth,
                    onMonthClick = { selectedMonth = it }
                )
            } else {
                GalleryScreen(
                    multimediaItems = groupedByMonth[selectedMonth].orEmpty(),
                    onBack = { selectedMonth = null }
                )
            }
        }
    }
}

@Composable
private fun MultimediaTopAppBar(title: String, onBack: () -> Unit) {
    Header(attrs = {
        style {
            width(100.percent)
            height(90.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            position(Position.Relative)
        }
    }) {
        Button(attrs = {
            onClick { onBack() }
            style {
                position(Position.Absolute)
                left(16.px)
                backgroundColor(Color("transparent"))
                color(White)
                border(0.px)
                fontSize(24.px)
                cursor("pointer")
                padding(8.px)
            }
        }) { Text("⬅️") } // Usamos emoji o carácter Unicode nativo

        H2(attrs = {
            style { color(White); margin(0.px); fontSize(24.px); fontWeight("bold") }
        }) { Text(title) }
    }
}

@Composable
private fun MultimediaTabs(selectedTabIndex: Int, onTabSelected: (Int) -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            property("border-bottom", "1px solid gray")
            marginBottom(16.px)
        }
    }) {
        @Composable
        fun tab(text: String, isSelected: Boolean, onClick: () -> Unit) {
            Div(attrs = {
                onClick { onClick() }
                style {
                    flex(1)
                    textAlign("center")
                    padding(12.px)
                    cursor("pointer")
                    color(if (isSelected) VspMarco else White)
                    property("border-bottom", "2px solid ${if (isSelected) VspMarco.toString() else "transparent"}")
                    fontWeight(if (isSelected) "bold" else "normal")
                    fontSize(16.px)
                }
            }) { Text(text) }
        }
        
        tab("Imágenes", selectedTabIndex == 0) { onTabSelected(0) }
        tab("Videos", selectedTabIndex == 1) { onTabSelected(1) }
    }
}

@Composable
private fun MonthFoldersScreen(
    groupedByMonth: Map<String?, List<MultimediaItem>>,
    onMonthClick: (String?) -> Unit
) {
    val monthList = remember(groupedByMonth) { groupedByMonth.entries.sortedByDescending { it.key } }

    // Reemplazo de LazyVerticalGrid por CSS Grid nativo
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(140px, 1fr))")
            gap(16.px)
            width(100.percent)
        }
    }) {
        monthList.forEach { (monthKey, itemsInMonth) ->
            FolderCard(
                monthKey = monthKey,
                previewItem = itemsInMonth.firstOrNull(),
                onClick = { onMonthClick(monthKey) }
            )
        }
    }
}

@Composable
private fun FolderCard(monthKey: String?, previewItem: MultimediaItem?, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            position(Position.Relative)
            property("aspect-ratio", "1 / 1")
            backgroundColor(rgba(80, 80, 80, 0.5))
            borderRadius(8.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            cursor("pointer")
            property("overflow", "hidden")
        }
    }) {
        if (previewItem != null) {
            Span(attrs = { style { fontSize(48.px) } }) {
                Text(if (previewItem.mimeType.startsWith("image/")) "🖼️" else "▶️")
            }
        } else {
            Span(attrs = { style { color(White) } }) { Text("Sin ítems") }
        }
        
        Div(attrs = {
            style {
                position(Position.Absolute)
                bottom(0.px)
                width(100.percent)
                backgroundColor(rgba(0, 0, 0, 0.7))
                color(White)
                textAlign("center")
                padding(8.px, 0.px)
                fontWeight("bold")
                fontSize(14.px)
            }
        }) { Text(monthKey ?: "Desconocido") }
    }
}

@Composable
private fun GalleryScreen(multimediaItems: List<MultimediaItem>, onBack: () -> Unit) {
    var currentItem by remember { mutableStateOf<MultimediaItem?>(null) }

    Div(attrs = {
        style { width(100.percent); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) }
    }) {
        Span(attrs = {
            onClick { onBack() }
            style { color(VspMarco); fontSize(16.px); fontWeight("bold"); cursor("pointer"); marginBottom(16.px); display(DisplayStyle.InlineBlock) }
        }) { Text("< Volver a Carpetas") }

        // CSS Grid para la galería
        Div(attrs = {
            style { display(DisplayStyle.Grid); property("grid-template-columns", "repeat(auto-fill, minmax(120px, 1fr))"); gap(12.px); width(100.percent) }
        }) {
            multimediaItems.forEach { item ->
                FolderCard(monthKey = item.name, previewItem = item, onClick = { currentItem = item }) // Reutilizamos el estilo de FolderCard como miniatura
            }
        }
    }

    currentItem?.let { media ->
        MediaFullScreenViewer(media = media, onDismiss = { currentItem = null })
    }
}

@Composable
private fun MediaFullScreenViewer(media: MultimediaItem, onDismiss: () -> Unit) {
    // Modal Screen de Pantalla Completa
    Div(attrs = {
        style { position(Position.Fixed); top(0.px); left(0.px); width(100.vw); height(100.vh); backgroundColor(rgba(0, 0, 0, 0.9)); property("z-index", 1000)
            display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center); flexDirection(FlexDirection.Column) }
    }) {
        Button(attrs = {
            onClick { onDismiss() }
            style { position(Position.Absolute); top(16.px); right(16.px); padding(10.px, 24.px); backgroundColor(VspMarco); color(White)
                border(0.px); borderRadius(8.px); cursor("pointer"); fontSize(16.px); property("z-index", 1001) }
        }) { Text("Cerrar") }

        // Aprovechamos los reproductores nativos del navegador que ya incluyen zoom, play, volumen, etc.
        if (media.mimeType.startsWith("image/")) {
            Img(src = media.url, attrs = {
                style { maxWidth(95.percent); maxHeight(90.percent); property("object-fit", "contain") }
            })
        } else {
            // Video Nativo de HTML5
            Video(attrs = {
                attr("controls", "true")
                attr("autoplay", "true")
                style { maxWidth(95.percent); maxHeight(90.percent); outline("none") }
            }) {
                Source(attrs = { attr("src", media.url); attr("type", media.mimeType) })
            }
        }
    }
}